import React, { useState } from 'react';
import {
  FaChartBar,
  FaInfoCircle,
  FaLightbulb,
  FaVolumeUp,
  FaDownload,
  FaCopy,
  FaClipboardList,
  FaFlask,
  FaBoxes,
  FaDollarSign,
  FaBook
} from 'react-icons/fa';
import './css/style.scss'; 
import config from '../../../configs/config';

const businessSummaryData = [
   {
    title: 'Overall Summary',
    content: [
                'In Q4 2024, cost per loan disbursement decreased by 3.52%, while write-offs/recoveries reduced sharply by 79.2% from the average of the last six months. Operating income increased by 26.65%, and operating expenses rose by 20.28% indicating overall growth, despite a 3.49% decrease in effective tax rate.'] 
    },
  {
    title: 'Revenue Drivers & Leakages - Q4 2024 Performance',
    icon: <FaChartBar size={14} />,
    content: [
    'In Q4 2024, revenue growth was driven by higher net interest income, with Asia rising 6.18% and Americas 5.68%, alongside profit before tax increases of 4.43% and 4.22%, respectively. Operating income grew in both regions, supported by rising customer deposits in Asia (+8.32%).Leakages included rising operating expenses (+6.56% Asia, +6.02% Americas) and modest increases in cost-to-income ratios. Credit impairment charges fell slightly, and write-offs dropped sharply by up to 79%, boosting recovery. Overall, robust revenue growth was tempered by cost pressures and credit risk management improvements.']  
    },
  {
    title: 'Operational & Business Unit Signals - Q4 2024 Performance:',
    icon: <FaInfoCircle size={14} />,
    content: ['Asia delivered strong cost controls, with cost per loan disbursement down 3.53% and operating margins decreasing by 1.57%. Profit before tax rose by 4.43%, net interest income was up by 6.18%, while operating income surged by 5.28% for the region. In the Americas, operating expenses increased by 6.02% and profit after tax climbed 5.72%, but credit impairment charges dropped by 2.34%.']  
  },
  {
    title: 'Product & Segment Performance - Q4 2024 Performance:',
    icon: <FaLightbulb size={14} />,
    content: [
        'Corporate Investment Banking achieved a notable reduction in write-offs/recoveries by 78.13%, while Wealth Retail Banking reduced these by 79.92%. Net interest income increased 5.68% for Americas, augmenting stable product-level growth. Transaction services observed efficient cost management reflected by cost per loan disbursement and cost-to-income ratio improvements.'    ]
  },
   {
    title: 'Market & Regional Challenges - Q4 2024 Performance:',
    icon: <FaLightbulb size={14} />,
    content: [
    'Asia faced margin compression, with operating margin falling by 1.57% and cost-to-income ratios rising by 0.86%. In the Americas, there was a positive swing as cost per loan disbursement decreased by 3.51% and cost-to-income ratio increased by 0.86%. Both regions saw mixed signals for profitability and risk metrics, facing challenging market conditions and regulatory adjustments in Q4.']
  }
];
const insightIconMap = {
  Overall: <FaClipboardList size={14} />,
  Reputation: <FaFlask size={14} />,
  'Product Performance': <FaBoxes size={14} />,
  Profitability: <FaDollarSign size={14} />,
  Compliance: <FaBook size={14} />
};

const submitQuery = (query, type, id) => {
  let url =
    (config.prefix ? '/' + config.prefix_name : '') +
    '/chat-bank?query=' +
    query.replaceAll('%', ' percentage') +
    '&type=' +
    type;

  if (id) {
    url += '&id=' + id;
  }
  window.location = url;
};

const keyInsightsData = [
  {
    title: 'Asia’s Cost Efficiency Gains',
    content:
      'Cost per loan disbursement in Asia decreased by 3.53%, driving enhanced operational efficiency despite a slight 1.57% dip in operating margin, reflecting tighter cost controls amid rising net interest income (+6.18%).',
    prompts: [
      'Show Asia’s Cost per loan disbursement in Q4 2024?'
    ]
  },
  {
    title: 'Americas Operating Expense Pressure',
    content:
      'Operating expenses in the Americas rose by 6.02% while credit impairment charges declined by 2.34%, highlighting growing cost pressures offset by improved risk management performance.',
    prompts: [
      'What was the increase in operating expenses in the Americas during Q4 2024?'
    ]
  },
  {
    title: 'Sharp Reduction in Write-Offs',
    content:
      'Write-offs and recoveries fell significantly by 79.2% overall, signaling strong credit recovery efforts and improved asset quality across key segments and regions.',
    prompts: [
      'Describe the trend of write-off reductions for Q4 2024?'
    ]
  },
  {
    title: 'Strong Revenue Growth in Net Interest Income',
    content:
      'Asia and Americas both saw increases in net interest income by 6.18% and 5.68%, respectively, playing a key role in profit before tax growth of 4.43% and 4.22%.',
    prompts: [
      'Show net interest income and profit before tax in Q4 2024?'
    ]
  },
  {
    title: 'Mixed Profitability Signals Amid Regional Challenges',
    content:
      'Operating margin contraction in Asia (-1.57%) and slight increases in cost-to-income ratios indicate ongoing market challenges despite volume-driven revenue growth.',
    prompts: [
      'Show cost-to-income ratio and Operating margin trends in Q4 2024?'
    ]
  }
];

const BusinessNarratives = () => {
  const [selectedTab, setSelectedTab] = useState(0);

  // Conditionally add the transparent-bg class
  const contentCardClass = `content-card ${selectedTab === 1 ? 'transparent-bg' : ''}`;

  return (
    <div className="business-narratives-container">
      {/* Gradient Header */}
      <div className="header-gradient">
        <h3>Curated key summaries for you!</h3>
        <h4 className="key-title">Key Narratives</h4>

        {/* Tabs */}
        <div className="tabs">
          <button
            className={selectedTab === 0 ? 'active' : ''}
            onClick={() => setSelectedTab(0)}
          >
            Business Summary
          </button>
          <button
            className={selectedTab === 1 ? 'active' : ''}
            onClick={() => setSelectedTab(1)}
          >
            Key Insights
          </button>
        </div>

        {selectedTab === 1 && (
          <div className="insight-note">
            <FaInfoCircle style={{ marginRight: '6px' }} />
            <span>Click on any conversation tags to initiate an analysis</span>
          </div>
        )}
      </div>

      {/* White Card Content */}
      <div className={contentCardClass}>
        {selectedTab === 0 && (
          <>
            {/* Title + Icons Row */}
            <div className="content-header">
              <h4 style={{fontSize:'1rem'}}>Business Performance Summary</h4>
              <div className="icons">
                <FaVolumeUp />
                <FaDownload />
                <FaCopy />
              </div>
            </div>

            <div>
              {businessSummaryData.map((section, index) => (
                <div key={index} className="summary-section">
                  <div className="section-title">
                    {section.icon}
                    <span>{section.title}</span>
                  </div>
                  <p>{section.content}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {selectedTab === 1 && (
          <div className="key-insights-grid">
            {keyInsightsData.map((insight, index) => (
              <div key={index} className="insight-card">
                <div className="section-title">
                  {insightIconMap[insight.title] || <FaChartBar size={14} />}
                  <strong>{insight.title}</strong>
                </div>
                <p>{insight.content}</p>
                <div className="prompts">
                  {insight.prompts.map((prompt, i) => (
                    <button
                      key={i}
                      onClick={() => submitQuery(prompt, 'insight')}
                      className="prompt-btn"
                    >
                      {prompt}
                    </button>
                  ))}
                  <button className="more-btn">+{insight.prompts.length}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessNarratives;